import React from 'react';
import moment from 'moment';
// TODO: APP-3570 Remove when rets2 decodes
import { decode } from 'html-entities';
import { useLocation } from 'react-router-dom';

import { ParsedListing } from '@bb/domain/dist/types';
import { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from 'hooks';
import {
  AuthContext,
  parseLabelFromString,
  ListingLabelContext,
  ListingLabelContextType,
  AuthContextUser,
} from 'contexts';
import {
  Thumbnail,
  Text,
  Paragraph,
  PriceTag,
  Skeleton,
  Contact,
  Availability,
  Tooltip,
  theme,
  Switch,
  Icon,
} from 'components';
import {
  formatCurrency,
  addDatacyToCell,
  addDatacyToTableHeader,
  isFullAccessUser,
} from 'utils';
import styled from 'styled-components';
import { TablePaginationConfig, TableProps } from 'antd';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import BasicListingTable from './BasicListingTable';
import EnhancedFilterDropdown from './EnhancedFilterDropdown';
import UseAppointmentDeskToggle from './UseAppointmentDeskToggle';

const columns: (
  { getLabel, getBoardName }: ListingLabelContextType,
  isFromLiveConciergePage?: boolean,
  filtersOption?: Record<
    string,
    { text: string; value: string | number | boolean }[]
  >, // useAppointmentDesk | availability | location
  auth?: AuthContextUser,
) => ColumnsType<ParsedListing & { isUnclaimed?: boolean }> = (
  { getLabel, getBoardName },
  isFromLiveConciergePage = false,
  filtersOption,
  auth,
) => {
  const displayedField =
    getLabel('Neighborhood Display')?.trim().toUpperCase() === 'COUNTY'
      ? 'county'
      : 'neighborhood';
  return (
    [
      // On the Live Concierge "Your Subscription" table, "View" and "Address"
      // are combined into a single "Listing" column (thumbnail + address).
      // Every other listing table keeps them as two separate columns.
      ...(isFromLiveConciergePage
        ? [
            {
              title: 'Listing',
              dataIndex: 'line1',
              key: 'line1',
              width: 300,
              render: (
                line1: string,
                listing: ParsedListing & {
                  isUnclaimed?: boolean;
                  thumbnail?: string;
                },
              ) => (
                <StyledListingCell>
                  <Thumbnail
                    src={listing.thumbnail}
                    listing={listing}
                    isPreviewDisabled={
                      listing.isUnclaimed && !isFullAccessUser(auth)
                    }
                  />
                  <StyledCellContent $maxWidth={200}>
                    <Paragraph
                      strong
                      ellipsis={{ rows: listing.line2 ? 3 : 1 }}
                      hideMargin
                    >
                      {decode(line1)} {decode(listing.line2 ?? '')}
                    </Paragraph>
                    <Text type="secondary" data-cy={listing.id} ellipsis block>
                      {listing.city}, {listing.province}, {listing.postal_code}
                    </Text>
                  </StyledCellContent>
                </StyledListingCell>
              ),
              onHeaderCell: addDatacyToTableHeader('listing'),
            },
          ]
        : [
            {
              title: 'View',
              dataIndex: 'thumbnail',
              key: 'thumbnail',
              render: (
                value: string,
                listing: ParsedListing & { isUnclaimed?: boolean },
              ) => (
                <Thumbnail
                  src={value}
                  listing={listing}
                  isPreviewDisabled={
                    listing.isUnclaimed && !isFullAccessUser(auth)
                  }
                />
              ),
              onHeaderCell: addDatacyToTableHeader('listing-view'),
            },
            {
              title: 'Address',
              dataIndex: 'line1',
              key: 'line1',
              render: (
                line1: string,
                { line2 = '', city, province, postal_code, id }: any,
              ) => (
                <StyledCellContent $maxWidth={200}>
                  <Paragraph
                    strong
                    ellipsis={{
                      rows: line2 ? 3 : 1,
                    }}
                    hideMargin
                  >
                    {decode(line1)} {decode(line2)}
                  </Paragraph>
                  <Text type="secondary" data-cy={id} ellipsis block>
                    {city}, {province}, {postal_code}
                  </Text>
                </StyledCellContent>
              ),
            },
          ]),
      {
        title: 'Price',
        dataIndex: 'list_price',
        key: 'list_price',
        render: (
          price: number,
          { sold_details, leased_details, availability },
        ) => {
          const getPrice = (sold: boolean, leased: boolean) => {
            if (sold) return sold_details?.price;
            if (leased) return leased_details?.price;
            return price;
          };
          const sold = sold_details?.price && availability === 'Sold';
          const leased = leased_details?.price && availability === 'Leased';
          const hasTooltip = Boolean(sold || leased);
          const Wrapper: any = hasTooltip ? Tooltip : StyledIgnoreCursor;
          const success = hasTooltip ? true : null;
          const wrapperProps = hasTooltip
            ? {
                title: (
                  <div>
                    <div>Listed: {formatCurrency(price)}</div>
                    {sold && (
                      <div>Sold: {formatCurrency(sold_details.price)}</div>
                    )}
                    {leased && (
                      <div>Leased: {formatCurrency(leased_details.price)}</div>
                    )}
                  </div>
                ),
                placement: 'bottom',
                getPopupContainer: (node: HTMLElement) => node.parentElement,
              }
            : null;

          return (
            <StyledCellContent $minWidth={50}>
              <Wrapper {...wrapperProps}>
                <span>
                  <PriceTag
                    price={getPrice(sold, leased)}
                    hideMargin
                    success={success}
                  />
                </span>
              </Wrapper>
            </StyledCellContent>
          );
        },
      },
      {
        title: getLabel('Neighborhood'),
        dataIndex: displayedField,
        key: displayedField,
        width: 170,
        render: neighborhood => (
          <Paragraph hideMargin ellipsis={{ rows: 2 }}>
            {neighborhood}
          </Paragraph>
        ),
      },
      {
        title: 'Listing ID',
        dataIndex: 'status',
        key: 'status',
        ellipsis: true,
        render: (status, { mls_numbers, coming_soon_date }) => (
          <StyledCellContent $minWidth={90}>
            {mls_numbers ? (
              mls_numbers.map(({ board, number }, i) => (
                <Text ellipsis key={i} block>
                  {getBoardName(board)}: {number}
                </Text>
              ))
            ) : (
              <div>{getLabel(status)}</div>
            )}
            {status === 'Coming Soon' && (
              <div>{moment(coming_soon_date).format('MMM D, YYYY')}</div>
            )}
          </StyledCellContent>
        ),
      },
      {
        title: 'Branch',
        dataIndex: 'location',
        key: 'location',
        filters: filtersOption?.location,
        filterDropdown:
          filtersOption &&
          ((props: FilterDropdownProps) => (
            <EnhancedFilterDropdown {...props} allowSearch />
          )),
        width: '11%',
        render: (location: string) =>
          auth?.org?.locations[parseInt(location, 10)]?.name,
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        render: (type: string) => <Text ellipsis>{getLabel(type)}</Text>,
      },
      {
        title: 'Dom',
        dataIndex: 'dom',
        key: 'dom',
        width: 70,
      },
      {
        title: `${getLabel('Availability')}`,
        dataIndex: 'availability',
        key: 'availability',
        filters: filtersOption?.availability,
        filterDropdown: filtersOption && EnhancedFilterDropdown,
        render: availability => (
          <StyledCellContent $minWidth={105}>
            <Availability availability={availability} />
          </StyledCellContent>
        ),
      },
      {
        title: 'Expiry',
        dataIndex: 'expiry',
        key: 'expiry',
        render: expiry => moment(expiry).format('MMM D, YYYY'),
      },
      {
        title: 'Agents',
        dataIndex: 'fullReps',
        key: 'fullReps',
        ellipsis: true,
        width: isFromLiveConciergePage ? 170 : 200,
        ...(!isFromLiveConciergePage && {
          fixed: 'right',
          onHeaderCell: () => ({
            style: {
              borderLeft: `1px solid ${theme.gray3}`,
            },
            ...addDatacyToTableHeader('listing-agents'),
          }),
          onCell: () => ({
            onClick: e => {
              e.stopPropagation();
            },
            style: {
              borderLeft: `1px solid ${theme.gray3}`,
              cursor: 'default',
            },
            ...addDatacyToCell('listing-agents'),
          }),
        }),
        render: (fullReps: ParsedListing['fullReps'], { id }) => (
          <span
            onClick={e => e.stopPropagation()}
            data-cy={`listing-agents-${id}`}
          >
            {fullReps.map(rep => (
              <Contact
                key={rep.id}
                contact={rep}
                {...(isFromLiveConciergePage && {
                  viewNameOnToolTip: true,
                  disableClick: true,
                })}
              />
            ))}
          </span>
        ),
      },
      {
        title: 'Enabled',
        dataIndex: 'useAppointmentDesk',
        key: 'useAppointmentDesk',
        align: 'center',
        width: 120,
        filters: filtersOption?.useAppointmentDesk,
        filterDropdown: filtersOption && EnhancedFilterDropdown,
        onCell: () => ({
          onClick: e => e.stopPropagation(),
        }),
        render: (useAppointmentDesk, { id, line1, line2 = '' }) => (
          <StyledInlineBlock onClick={e => e.stopPropagation()}>
            <UseAppointmentDeskToggle
              listingId={id}
              useAppointmentDesk={useAppointmentDesk}
              address={`${decode(line1)} ${decode(line2)}`}
            />
          </StyledInlineBlock>
        ),
        onHeaderCell: addDatacyToTableHeader('listing-enable'),
      },
    ] as ColumnsType<ParsedListing>
  ).filter(({ key }) =>
    isFromLiveConciergePage
      ? !['neighborhood', 'type', 'dom', 'expiry', 'fullReps'].includes(
          key as string,
        )
      : !['useAppointmentDesk', 'location'].includes(key as string),
  );
};

const sortableKeys: (keyof ParsedListing)[] = [
  'line1',
  'list_price',
  'neighborhood',
  'status',
  'type',
  'dom',
  'availability',
  'expiry',
  'location',
];

type ListingTableProps = {
  listings: ParsedListing[];
  loading: boolean;
  setRedirect: (address: string) => void;
  sort?: (field: string) => void;
  sortField?: React.ReactText;
  sortOrder?: 'ascend' | 'descend' | null;
  showSorter?: boolean;
  isFromLiveConciergePage?: boolean;
  filtersOption?: Record<
    string,
    { text: string; value: string | number | boolean }[]
  >;

  // used for child component only
  pagination?: false | TablePaginationConfig;
  heightOffset: number;
  selectedRowKeys?: React.ReactText[];
  setSelectedRowKeys?: (keys: string[]) => void;
  onChange?: TableProps<ParsedListing>['onChange'];
};

const ListingTable: React.FC<ListingTableProps> = ({
  listings,
  loading,
  setRedirect,
  sort,
  sortField,
  sortOrder,
  showSorter,
  isFromLiveConciergePage,
  filtersOption,
  ...props
}) => {
  const location = useLocation();
  const { auth } = React.useContext(AuthContext);
  const listingLabelContext = React.useContext(ListingLabelContext);
  const { getAvailability } = listingLabelContext;

  const { md } = useBreakpoint();

  const getStyle = (key: string, isHeader?: boolean) => {
    if (sortField !== key) return {};
    return {
      backgroundColor: isHeader ? theme.gray3 : theme.gray2,
    };
  };

  const showExpiry = () => {
    if (!auth) return false;
    const { role } = auth;
    return !['AGENT', 'TEAM_ADMIN'].includes(role);
  };

  const preparedDataSource = React.useMemo(() => {
    interface PreparedListing extends ParsedListing {
      key: string;
    }
    return listings.map<PreparedListing>(listing => ({
      key: listing._id.toString(),
      ...listing,
    }));
  }, [listings]);

  const preparedColumns = React.useMemo(() => {
    const renderDesktop = () =>
      columns(
        listingLabelContext,
        Boolean(isFromLiveConciergePage),
        filtersOption,
        auth,
      )
        .filter(({ key }) => key !== 'expiry' || showExpiry())
        .map(column => {
          const key = column.key.toString();
          if (sortableKeys.includes(key as keyof ParsedListing)) {
            return {
              ...column,
              onHeaderCell: () => ({
                ...(sort && {
                  onClick: () => sort(key),
                  style: {
                    cursor: 'pointer',
                    ...getStyle(key, true),
                  },
                }),
                ...addDatacyToTableHeader(key)(),
              }),

              onCell: (data: any) => ({
                style: getStyle(key),
                ...addDatacyToCell(key)(data),
              }),

              ...(showSorter && {
                sorter: true,
                sortOrder: sortField === key ? sortOrder : null,
                showSorterTooltip: true,
              }),
            };
          }
          return column;
        });
    const renderMobile = () =>
      [
        {
          title: '',
          dataIndex: 'line1',
          key: 'line1',
          render: (
            line1,
            { thumbnail, line2, list_price, mls_numbers, status, availability },
          ) => (
            <StyledMobileCell>
              <Thumbnail src={thumbnail} />
              <div>
                <Text strong ellipsis>
                  {line1} {line2}
                </Text>
              </div>
              <div>
                <Text ellipsis>
                  <PriceTag price={list_price} />
                  <Text type="secondary">
                    {getAvailability(availability, { condensed: true })}
                  </Text>
                  <StyledCircle type="circle" width={5} />
                  {mls_numbers ? (
                    mls_numbers.map(({ number }, i) => (
                      <Text type="secondary" key={i}>
                        {number}
                      </Text>
                    ))
                  ) : (
                    <Text type="secondary">{status}</Text>
                  )}
                </Text>
              </div>
            </StyledMobileCell>
          ),
        },
      ] as ColumnsType<ParsedListing>;
    const renderLoadingDesktop = () =>
      columns(
        listingLabelContext,
        Boolean(isFromLiveConciergePage),
        filtersOption,
        auth,
      ).map(_column => {
        const { key } = _column;
        const column = { ..._column };
        if (sortableKeys.includes(key as keyof ParsedListing) && showSorter) {
          column.sorter = true;
          column.sortOrder = sortField === key ? sortOrder : null;
        }

        if (
          ['type', 'dom', 'availability', 'expiry'].includes(key.toString())
        ) {
          return {
            ...column,
            ...(!isFromLiveConciergePage && { width: 230 }),
            render: () => (
              <Skeleton title={false} paragraph={{ rows: 1, width: '100%' }} />
            ),
          };
        }
        if (key === 'line1') {
          return {
            ...column,
            ...(!isFromLiveConciergePage && { width: 200 }),
            render: () =>
              isFromLiveConciergePage ? (
                // The combined "Listing" column: thumbnail + address skeletons.
                <StyledListingCell>
                  <Thumbnail loading />
                  <Skeleton
                    title={false}
                    paragraph={{ rows: 2, width: ['100%', '90%'] }}
                  />
                </StyledListingCell>
              ) : (
                <Skeleton
                  title={false}
                  paragraph={{ rows: 2, width: ['100%', '90%'] }}
                />
              ),
          };
        }
        if (['neighborhood', 'status'].includes(key.toString())) {
          return {
            ...column,
            ...(!isFromLiveConciergePage && { width: 170 }),
            render: () => (
              <Skeleton
                title={false}
                paragraph={{ rows: 2, width: ['60%', '90%'] }}
              />
            ),
          };
        }
        if (key === 'fullReps') {
          return {
            ...column,
            ...(!isFromLiveConciergePage && { width: 200 }),
            render: () => (
              <>
                <Contact loading />
                <Contact loading />
              </>
            ),
          };
        }
        if (key === 'thumbnail') {
          return {
            ...column,
            render: () => <Thumbnail loading />,
          };
        }
        if (key === 'list_price') {
          return {
            ...column,
            render: () => <PriceTag loading />,
          };
        }
        if (key === 'useAppointmentDesk') {
          return {
            ...column,
            render: () => (
              <StyledInlineBlock>
                <Switch loading />
              </StyledInlineBlock>
            ),
          };
        }
        if (key === 'location') {
          return {
            ...column,
            render: () => (
              <Skeleton title={false} paragraph={{ rows: 1, width: ['90%'] }} />
            ),
          };
        }
        return column;
      });
    const renderLoadingMobile = () =>
      [
        {
          title: '',
          dataIndex: 'line1',
          key: 'line1',
          render: () => (
            <StyledMobileCell>
              <Thumbnail loading />
              <div>
                <Skeleton
                  title={false}
                  paragraph={{ rows: 2, width: [150, 220] }}
                />
              </div>
            </StyledMobileCell>
          ),
        },
      ] as ColumnsType<ParsedListing>;

    if (md) {
      if (!loading) return renderDesktop();
      return renderLoadingDesktop();
    }
    if (!loading) return renderMobile();
    return renderLoadingMobile();
  }, [loading, sortField, md, listingLabelContext, filtersOption]);

  const { search, pathname } = location;
  const page = pathname.split('/').reverse()[0];

  const handleRowClick = (key: string) => {
    const listingPerPage = 20;
    const listingIndex = preparedDataSource.findIndex(item => item.id === key);
    const index = parseInt(page, 10) * listingPerPage + listingIndex;
    if (!loading) {
      setRedirect(
        `/listing/${key}/view${search}${
          !Number.isNaN(index) ? `&index=${index}` : ''
        }`,
      );
    }
  };

  return (
    <BasicListingTable
      /**
       * Temporarily disabled for [SUPRANN-161]
       * allowSelection={md && !isFromLiveConciergePage}
       */
      handleRowClick={handleRowClick}
      dataSource={preparedDataSource}
      columns={preparedColumns}
      loading={loading}
      isFromLiveConciergePage={isFromLiveConciergePage}
      {...props}
    />
  );
};

export default React.memo(ListingTable);

const StyledInlineBlock = styled.span`
  display: inline-block;
`;

const StyledListingCell = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const StyledCircle = styled(Icon)`
  margin: 5px;
  vertical-align: text-bottom;
`;

const StyledCellContent = styled.div<{
  $minWidth?: number;
  $maxWidth?: number;
}>`
  ${({ $minWidth, $maxWidth }) => `
    min-width: ${$minWidth}px;
    max-width: ${$maxWidth}px;
  `}
`;

const StyledIgnoreCursor = styled.span`
  pointer-events: none;
`;

const StyledMobileCell = styled.div`
  display: grid;
  grid-template-columns: 65px calc(100% - 65px);
  grid-column-gap: 10px;

  div:first-child {
    grid-row-end: span 2;
  }
  ${Text} {
    max-width: 100%;
  }
  ${Text}.line2 {
    margin-left: 10px;
  }
`;
